import { Injectable } from '@angular/core';
import { INode } from '@yfiles/yfiles';
import {
  BranchPath,
  BranchRegion,
  BusinessProcessAnalysisResult,
  ProcessConnection,
  ProcessNodeTag,
  ProcessStructuralRole,
} from '../models/business-process.model';
@Injectable({ providedIn: 'root' })
export class BranchRegionAnalyzer {
  private s = new Map<INode, INode[]>();
  private p = new Map<INode, INode[]>();
  analyze(
    nodes: Iterable<INode>,
    edges: readonly ProcessConnection[],
  ): BusinessProcessAnalysisResult {
    const ns = Array.from(nodes);
    this.build(ns, edges);
    this.roles(ns);
    const ipdom = this.postdominators(ns),
      regions: BranchRegion[] = [];
    for (const b of ns) {
      const ss = this.S(b);
      if (ss.length < 2) continue;
      const r = ipdom.get(b);
      if (!r) continue;
      const paths: BranchPath[] = ss.map((firstNode, index) => ({
        index,
        firstNode,
        nodes: this.until(firstNode, r),
      }));
      const members = new Set(paths.flatMap((x) => x.nodes));
      regions.push({
        id: `branch-${regions.length + 1}`,
        branch: b,
        rejoin: r,
        paths,
        members,
      });
    }
    regions.sort((a, b) => a.members.size - b.members.size);
    const order = new Map<INode, number>();
    for (const r of regions)
      for (const path of r.paths)
        for (const n of path.nodes) if (!order.has(n)) order.set(n, path.index);
    return { regions, branchPathOrder: order };
  }
  private build(ns: INode[], es: readonly ProcessConnection[]) {
    this.s.clear();
    this.p.clear();
    for (const n of ns) {
      this.s.set(n, []);
      this.p.set(n, []);
    }
    for (const e of es) {
      this.s.get(e.source)!.push(e.target);
      this.p.get(e.target)!.push(e.source);
    }
  }
  private roles(ns: INode[]) {
    for (const n of ns) {
      const i = this.P(n).length,
        o = this.S(n).length;
      const role: ProcessStructuralRole =
        i > 1 && o > 1
          ? 'branch-and-rejoin'
          : o > 1
            ? 'branch'
            : i > 1
              ? 'rejoin'
              : 'normal';
      // kept on the graph node's tag, beside its data, as it belongs to this layout of the graph
      (n.tag as ProcessNodeTag).structuralRole = role;
    }
  }
  // each node's immediate postdominator: the first node that every route from it to an end passes through. Found as
  // dominators of the reversed graph from a virtual end joined to every end node (Cooper, Harvey & Kennedy), which
  // keeps it quick for graphs of thousands of nodes. Nodes with none, or that never reach an end, are left out
  private postdominators(ns: INode[]): Map<INode, INode> {
    const END = null;
    type Item = INode | typeof END;
    // in the reversed graph a node's predecessors are its successors, and the end's are the end nodes
    const reversedSuccessors = (n: Item) =>
      n === END ? ns.filter((x) => !this.S(x).length) : this.P(n);
    const reversedPredecessors = (n: INode): Item[] =>
      this.S(n).length ? this.S(n) : [END];

    // reverse postorder of the reversed graph from the end, without recursion as processes can be long
    const order: Item[] = [],
      seen = new Set<Item>([END]),
      stack: { n: Item; next: Item[]; i: number }[] = [
        { n: END, next: reversedSuccessors(END), i: 0 },
      ];
    while (stack.length) {
      const top = stack[stack.length - 1];
      if (top.i < top.next.length) {
        const m = top.next[top.i++];
        if (!seen.has(m)) {
          seen.add(m);
          stack.push({ n: m, next: reversedSuccessors(m), i: 0 });
        }
      } else {
        order.push(top.n);
        stack.pop();
      }
    }
    order.reverse();
    const position = new Map<Item, number>(order.map((n, i) => [n, i]));

    const idom = new Map<Item, Item>([[END, END]]);
    const intersect = (a: Item, b: Item) => {
      while (a !== b) {
        while (position.get(a)! > position.get(b)!) a = idom.get(a)!;
        while (position.get(b)! > position.get(a)!) b = idom.get(b)!;
      }
      return a;
    };
    let changed = true;
    while (changed) {
      changed = false;
      for (const n of order) {
        if (n === END) continue;
        let d: Item | undefined;
        for (const p of reversedPredecessors(n)) {
          if (!idom.has(p)) continue;
          d = d === undefined ? p : intersect(p, d);
        }
        if (d !== undefined && idom.get(n) !== d) {
          idom.set(n, d);
          changed = true;
        }
      }
    }

    const result = new Map<INode, INode>();
    for (const [n, d] of idom) if (n !== END && d !== END) result.set(n, d);
    return result;
  }
  private until(start: INode, rejoin: INode) {
    const out: INode[] = [],
      seen = new Set<INode>(),
      q = [start];
    for (let i = 0; i < q.length; i++) {
      const n = q[i];
      if (n === rejoin || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
      for (const s of this.S(n)) if (s !== rejoin) q.push(s);
    }
    return out;
  }
  private S(n: INode) {
    return this.s.get(n) ?? [];
  }
  private P(n: INode) {
    return this.p.get(n) ?? [];
  }
}
